// src/components/canvas/nodes/VideoAudioMergeNode.js

import React, { memo, useState, useCallback, useEffect } from 'react';
import { useReactFlow, Handle, Position, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import FFmpegService from '../../../api/FFmpegService';
import ApiService from '../../../api/ApiService';
import { toast } from 'react-toastify';
import './VideoAudioMergeNode.css';

const apiService = new ApiService();

export const VideoAudioMergeNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { createNodeAndConnect, addNodesWithEdges } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();
  
  // 状态管理
  const [operationType, setOperationType] = useState(data.operationType || 'audioMerge'); // 'audioMerge' | 'videoConcat'
  const [mode, setMode] = useState(data.mode || 'replace');
  const [durationMode, setDurationMode] = useState(data.durationMode || 'shortest');
  const [audioVolume, setAudioVolume] = useState(data.audioVolume || 1.0);
  const [originalVolume, setOriginalVolume] = useState(data.originalVolume || 0.3);
  const [audioOffset, setAudioOffset] = useState(data.audioOffset || 0);
  const [fadeIn, setFadeIn] = useState(data.fadeIn || 0);
  const [fadeOut, setFadeOut] = useState(data.fadeOut || 0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState(null);
  
  // 连接检测状态
  const [hasVideoConnection, setHasVideoConnection] = useState(false);
  const [hasAudioConnection, setHasAudioConnection] = useState(false);
  const [videoConnectionCount, setVideoConnectionCount] = useState(0);

  // 获取连接的视频节点（单个）
  const getConnectedVideoUrl = useCallback(() => {
    console.log('[VideoAudioMerge] 获取连接的视频URL');
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'video'
    );
    
    console.log('[VideoAudioMerge] 视频连接边数量:', connectedEdges.length);
    
    if (connectedEdges.length === 0) {
      console.log('[VideoAudioMerge] 没有连接的视频节点');
      return null;
    }
    
    const sourceNode = getNode(connectedEdges[0].source);
    console.log('[VideoAudioMerge] 视频源节点:', {
      id: sourceNode?.id,
      type: sourceNode?.type,
      hasVideoUrl: !!sourceNode?.data?.videoUrl,
    });
    
    if (!sourceNode) {
      console.warn('[VideoAudioMerge] 警告: 找不到视频源节点');
      return null;
    }
    
    let videoUrl = null;
    if (sourceNode.type === 'videoTask') {
      videoUrl = sourceNode.data?.videoUrl;
      console.log('[VideoAudioMerge] 从 videoTask 获取视频URL:', videoUrl ? '成功' : '失败');
    } else if (sourceNode.type === 'videoReference') {
      videoUrl = sourceNode.data?.videoUrl;
      console.log('[VideoAudioMerge] 从 videoReference 获取视频URL:', videoUrl ? '成功' : '失败');
    } else {
      console.warn('[VideoAudioMerge] 警告: 不支持的视频节点类型:', sourceNode.type);
    }
    
    return videoUrl;
  }, [getEdges, getNode, id]);

  // 获取所有连接的视频URL（用于视频拼接）
  const getConnectedVideoUrls = useCallback(() => {
    console.log('[VideoAudioMerge] 获取所有连接的视频URL');
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'video'
    );
    
    console.log('[VideoAudioMerge] 视频连接边数量:', connectedEdges.length);
    
    const videoUrls = [];
    
    for (const edge of connectedEdges) {
      const sourceNode = getNode(edge.source);
      if (!sourceNode) continue;
      
      let videoUrl = null;
      if (sourceNode.type === 'videoTask') {
        videoUrl = sourceNode.data?.videoUrl;
      } else if (sourceNode.type === 'videoReference') {
        videoUrl = sourceNode.data?.videoUrl;
      }
      
      if (videoUrl) {
        videoUrls.push(videoUrl);
        console.log(`[VideoAudioMerge] 视频 ${videoUrls.length}: ${videoUrl.substring(0, 60)}...`);
      }
    }
    
    console.log('[VideoAudioMerge] 总共获取到视频URL数量:', videoUrls.length);
    return videoUrls;
  }, [getEdges, getNode, id]);

  // 获取连接的音频节点
  const getConnectedAudioUrl = useCallback(() => {
    console.log('[VideoAudioMerge] 获取连接的音频URL');
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'audio'
    );
    
    console.log('[VideoAudioMerge] 音频连接边数量:', connectedEdges.length);
    
    if (connectedEdges.length === 0) {
      console.log('[VideoAudioMerge] 没有连接的音频节点');
      return null;
    }
    
    const sourceNode = getNode(connectedEdges[0].source);
    
    if (!sourceNode) {
      console.warn('[VideoAudioMerge] 警告: 找不到音频源节点');
      return null;
    }
    
    // 输出完整的节点数据用于调试
    console.log('[VideoAudioMerge] 音频源节点详情:', {
      id: sourceNode.id,
      type: sourceNode.type,
      data: sourceNode.data,
    });
    
    let audioUrl = null;
    
    // 尝试从不同类型的节点获取音频URL
    if (sourceNode.type === 'audioTask') {
      audioUrl = sourceNode.data?.audioUrl;
      console.log('[VideoAudioMerge] 从 audioTask 获取音频URL:', audioUrl ? '成功' : '失败');
    } else if (sourceNode.type === 'audioReference') {
      audioUrl = sourceNode.data?.audioUrl;
      console.log('[VideoAudioMerge] 从 audioReference 获取音频URL:', audioUrl ? '成功' : '失败');
    } else if (sourceNode.type === 'textToAudio') {
      // 语音合成节点
      audioUrl = sourceNode.data?.audioUrl || sourceNode.data?.resultUrl || sourceNode.data?.outputUrl;
      console.log('[VideoAudioMerge] 从 textToAudio 获取音频URL:', audioUrl ? '成功' : '失败');
      if (!audioUrl) {
        console.warn('[VideoAudioMerge] textToAudio 节点数据中没有找到音频URL，检查的字段: audioUrl, resultUrl, outputUrl');
      }
    } else if (sourceNode.type === 'hailuoMusic') {
      // 音乐生成节点
      audioUrl = sourceNode.data?.audioUrl || sourceNode.data?.resultUrl || sourceNode.data?.outputUrl;
      console.log('[VideoAudioMerge] 从 hailuoMusic 获取音频URL:', audioUrl ? '成功' : '失败');
      if (!audioUrl) {
        console.warn('[VideoAudioMerge] hailuoMusic 节点数据中没有找到音频URL，检查的字段: audioUrl, resultUrl, outputUrl');
      }
    } else if (sourceNode.type === 'voiceClone') {
      // 语音克隆任务可能使用不同的字段名
      audioUrl = sourceNode.data?.audioUrl || sourceNode.data?.resultUrl || sourceNode.data?.outputUrl;
      console.log('[VideoAudioMerge] 从 voiceClone 获取音频URL:', audioUrl ? '成功' : '失败');
      if (!audioUrl) {
        console.warn('[VideoAudioMerge] voiceClone 节点数据中没有找到音频URL，检查的字段: audioUrl, resultUrl, outputUrl');
      }
    } else {
      // 尝试通用字段名
      console.warn('[VideoAudioMerge] 未识别的音频节点类型:', sourceNode.type);
      console.log('[VideoAudioMerge] 尝试从通用字段获取音频URL...');
      audioUrl = sourceNode.data?.audioUrl || 
                 sourceNode.data?.resultUrl || 
                 sourceNode.data?.outputUrl ||
                 sourceNode.data?.url;
      
      if (audioUrl) {
        console.log('[VideoAudioMerge] ✓ 从通用字段获取到音频URL');
      } else {
        console.error('[VideoAudioMerge] ✗ 无法从节点获取音频URL');
        console.error('[VideoAudioMerge] 可用的数据字段:', Object.keys(sourceNode.data || {}));
      }
    }
    
    if (audioUrl) {
      console.log('[VideoAudioMerge] 最终获取的音频URL:', audioUrl.substring(0, 100) + (audioUrl.length > 100 ? '...' : ''));
    }
    
    return audioUrl;
  }, [getEdges, getNode, id]);

  // 保存参数到节点数据
  useEffect(() => {
    updateNodeData(id, {
      operationType,
      mode,
      durationMode,
      audioVolume,
      originalVolume,
      audioOffset,
      fadeIn,
      fadeOut,
    });
  }, [id, operationType, mode, durationMode, audioVolume, originalVolume, audioOffset, fadeIn, fadeOut, updateNodeData]);

  // 检测连接状态
  useEffect(() => {
    const videoEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'video'
    );
    const audioEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'audio'
    );
    
    setHasVideoConnection(videoEdges.length > 0);
    setHasAudioConnection(audioEdges.length > 0);
    setVideoConnectionCount(videoEdges.length);
    
    console.log('[VideoAudioMerge] 连接状态更新:', {
      video: videoEdges.length,
      audio: audioEdges.length > 0
    });
  }, [edges, id]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    console.log('VideoAudioMergeNode - 验证连接:', {
      targetHandle: connection.targetHandle,
      source: connection.source,
      target: connection.target
    });
    
    // 视频输入 handle
    if (connection.targetHandle === 'video') {
      const sourceNode = getNode(connection.source);
      console.log('VideoAudioMergeNode - 视频源节点类型:', sourceNode?.type);
      const isValid = sourceNode?.type === 'videoTask' || sourceNode?.type === 'videoReference';
      
      if (!isValid) {
        toast.warning('视频输入只能连接视频任务节点或视频参考节点');
        return false;
      }
      
      // 音视频合并模式：只允许1个视频；视频拼接模式：允许多个
      if (operationType === 'audioMerge') {
        const currentEdges = getEdges();
        const videoEdgesCount = currentEdges.filter(
          edge => edge.target === id && edge.targetHandle === 'video'
        ).length;
        
        if (videoEdgesCount >= 1) {
          toast.warning('音视频合并模式只能连接一个视频节点');
          return false;
        }
      }
      
      return true;
    }
    
    // 音频输入 handle
    if (connection.targetHandle === 'audio') {
      const sourceNode = getNode(connection.source);
      console.log('VideoAudioMergeNode - 音频源节点类型:', sourceNode?.type);
      const validAudioTypes = ['audioReference', 'audioTask', 'textToAudio', 'hailuoMusic'];
      const isValid = validAudioTypes.includes(sourceNode?.type);
      
      if (!isValid) {
        toast.warning('音频输入只能连接音频参考节点、音频任务节点、语音合成节点或音乐生成节点');
        return false;
      }
      
      // 检查是否已经有音频连接（只允许1个）
      const currentEdges = getEdges();
      const audioEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'audio'
      ).length;
      
      if (audioEdgesCount >= 1) {
        toast.warning('音频输入只能连接一个节点');
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [getNode, getEdges, id, operationType]);

  // 处理合并/拼接
  const handleProcess = async () => {
    console.log('========================================');
    console.log(`[VideoAudioMerge] 开始${operationType === 'audioMerge' ? '音视频合并' : '视频拼接'}流程`);
    console.log('========================================');
    
    try {
      setError(null);
      setIsProcessing(true);
      setProgress(0);
      setStatusText('准备中...');

      // 检查 FFmpeg 加载状态
      console.log('[VideoAudioMerge] 检查 FFmpeg 加载状态');
      const status = FFmpegService.getLoadStatus();
      
      if (!status.loaded) {
        if (status.loading) {
          setStatusText('等待 FFmpeg 加载完成...');
          setProgress(5);
        } else {
          setStatusText('加载 FFmpeg（首次使用需下载 ~25MB）...');
          setProgress(0);
        }
        
        await FFmpegService.load((loadProgress) => {
          setProgress(loadProgress * 0.15);
          setStatusText(`加载 FFmpeg... ${loadProgress}%`);
        });
        console.log('[VideoAudioMerge] ✓ FFmpeg 加载完成');
      } else {
        setProgress(15);
      }

      let result;
      let taskType;

      if (operationType === 'videoConcat') {
        // 视频拼接模式
        console.log('[VideoAudioMerge] 执行视频拼接');
        const videoUrls = getConnectedVideoUrls();
        
        if (videoUrls.length < 2) {
          throw new Error('视频拼接需要至少连接2个视频节点');
        }
        
        setStatusText(`拼接 ${videoUrls.length} 个视频...`);
        setProgress(15);
        
        result = await FFmpegService.concatVideos(videoUrls, {
          onProgress: (concatProgress) => {
            setProgress(15 + concatProgress * 0.55);
          },
        });
        
        taskType = 'video-concat';
        console.log('[VideoAudioMerge] ✓ 视频拼接完成');
        
      } else {
        // 音视频合并模式
        console.log('[VideoAudioMerge] 执行音视频合并');
        const videoUrl = getConnectedVideoUrl();
        const audioUrl = getConnectedAudioUrl();

        if (!videoUrl) {
          throw new Error('请先连接视频节点');
        }

        if (!audioUrl) {
          throw new Error('请先连接音频节点');
        }
        
        setStatusText('合并视频和音频...');
        setProgress(15);

        result = await FFmpegService.mergeVideoAudio(
          videoUrl,
          audioUrl,
          {
            mode,
            durationMode,
            audioVolume,
            originalVolume,
            audioOffset,
            fadeIn,
            fadeOut,
            onProgress: (mergeProgress) => {
              setProgress(15 + mergeProgress * 0.55);
            },
          }
        );
        
        taskType = 'video-audio-merge';
        console.log('[VideoAudioMerge] ✓ 音视频合并完成');
      }

      console.log('[VideoAudioMerge] 结果信息:', {
        blobSize: `${(result.blob.size / 1024 / 1024).toFixed(2)} MB`,
        blobType: result.blob.type,
      });
      
      setProgress(70);

      // 上传到 Cloudflare R2
      console.log('[VideoAudioMerge] 上传到 Cloudflare R2');
      setStatusText('上传到 Cloudflare R2 云端...');
      setProgress(75);

      const fileName = `${operationType === 'videoConcat' ? 'concat' : 'merged'}-video-${Date.now()}.mp4`;
      const file = new File([result.blob], fileName, { type: 'video/mp4' });
      
      const uploadResult = await apiService.uploadImageToR2(file);
      
      setProgress(90);
      console.log('[VideoAudioMerge] ✓ 上传到 R2 成功');
      
      // 创建视频任务节点展示结果
      setStatusText('创建任务节点...');
      setProgress(95);
      
      try {
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: `${operationType}-${Date.now()}`,
            taskType: taskType,
            status: 'completed',
            videoUrl: uploadResult.public_url,
            inputParams: operationType === 'videoConcat' 
              ? { operationType, videoCount: getConnectedVideoUrls().length }
              : { mode, durationMode, audioVolume, originalVolume, audioOffset, fadeIn, fadeOut },
          },
        };
        
        const newEdge = {
          id: nanoid(),
          source: id,
          sourceHandle: 'output',
          target: taskNodeId,
          type: 'default',
        };
        
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('[VideoAudioMerge] ✓ 已创建视频任务节点:', taskNodeId);
      } catch (nodeError) {
        console.error('[VideoAudioMerge] 创建任务节点失败:', nodeError);
      }
      
      setProgress(100);
      setStatusText('完成');
      toast.success(operationType === 'videoConcat' 
        ? '视频拼接并上传成功！已创建任务节点' 
        : '视频合并并上传成功！已创建任务节点');

    } catch (error) {
      console.error('[VideoAudioMerge] 处理失败:', error);
      const errorMessage = error.message || error.toString() || '处理失败';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'videoAudioMerge', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <NodeLayout
      id={id}
      type="videoAudioMerge"
      title="音视频合并"
      showInputHandle={false}
      showOutputHandle={false}
    >
      {/* 自定义输入 Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="video"
        className="VideoAudioMergeNode-handle VideoAudioMergeNode-handle-video"
        style={{ left: '30%' }}
        isValidConnection={isValidConnection}
      >
        <div 
          className="VideoAudioMergeNode-handle-label VideoAudioMergeNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'video', 'video')}
          title="点击自动创建视频参考节点"
        >
          视频
        </div>
      </Handle>
      {/* 音频输入 Handle - 仅音视频合并模式显示 */}
      {operationType === 'audioMerge' && (
        <Handle
          type="target"
          position={Position.Top}
          id="audio"
          className="VideoAudioMergeNode-handle VideoAudioMergeNode-handle-audio"
          style={{ left: '70%' }}
          isValidConnection={isValidConnection}
        >
          <div 
            className="VideoAudioMergeNode-handle-label VideoAudioMergeNode-handle-label-clickable"
            onClick={(e) => handleHandleLabelClick(e, 'audio', 'audio')}
            title="点击自动创建音频参考节点"
          >
            音频
          </div>
        </Handle>
      )}

      <div className="VideoAudioMergeNode-wrapper">
        {/* 错误提示 */}
        {error && (
          <div className="VideoAudioMergeNode-error">
            ⚠️ {error}
          </div>
        )}

        {/* 操作类型选择 */}
        <div className="VideoAudioMergeNode-field">
          <label>操作类型</label>
          <select
            value={operationType}
            onChange={(e) => setOperationType(e.target.value)}
            disabled={readOnly || isProcessing}
            className="VideoAudioMergeNode-select VideoAudioMergeNode-select-primary"
          >
            <option value="audioMerge">🎵 音视频合并</option>
            <option value="videoConcat">🎬 视频拼接</option>
          </select>
        </div>

        {/* 连接状态检测 */}
        <div className="VideoAudioMergeNode-connection-status">
          <div className={`VideoAudioMergeNode-status-item ${hasVideoConnection ? 'connected' : 'disconnected'}`}>
            <span className="VideoAudioMergeNode-status-icon">
              {hasVideoConnection ? '✓' : '○'}
            </span>
            <span className="VideoAudioMergeNode-status-text">
              {operationType === 'videoConcat' 
                ? `视频 ${videoConnectionCount}个` 
                : (hasVideoConnection ? '视频已连接' : '等待连接视频')}
            </span>
          </div>
          {operationType === 'audioMerge' && (
            <div className={`VideoAudioMergeNode-status-item ${hasAudioConnection ? 'connected' : 'disconnected'}`}>
              <span className="VideoAudioMergeNode-status-icon">
                {hasAudioConnection ? '✓' : '○'}
              </span>
              <span className="VideoAudioMergeNode-status-text">
                {hasAudioConnection ? '音频已连接' : '等待连接音频'}
              </span>
            </div>
          )}
        </div>

        {/* 参数配置区域 */}
        <div className="VideoAudioMergeNode-config">
          {/* 音视频合并模式的配置 */}
          {operationType === 'audioMerge' && (
            <>
              {/* 合并模式 */}
              <div className="VideoAudioMergeNode-field">
                <label>合并模式</label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  disabled={readOnly || isProcessing}
                  className="VideoAudioMergeNode-select"
                >
                  <option value="replace">替换原音</option>
                  <option value="mix">混合音频</option>
                </select>
              </div>
            </>
          )}

          {/* 音视频合并模式的额外配置 */}
          {operationType === 'audioMerge' && (
            <>
              {/* 长度模式 */}
              <div className="VideoAudioMergeNode-field">
                <label>长度模式</label>
                <select
                  value={durationMode}
                  onChange={(e) => setDurationMode(e.target.value)}
                  disabled={readOnly || isProcessing}
                  className="VideoAudioMergeNode-select"
                >
                  <option value="shortest">自动（取较短）</option>
                  <option value="video">保持视频长度</option>
                  <option value="audio">保持音频长度</option>
                </select>
              </div>

              {/* 音频音量 */}
              <div className="VideoAudioMergeNode-field">
                <label>音频音量 ({Math.round(audioVolume * 100)}%)</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={audioVolume}
                  onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                  disabled={readOnly || isProcessing}
                  className="VideoAudioMergeNode-slider"
                />
              </div>

              {/* 原音音量（仅混合模式） */}
              {mode === 'mix' && (
                <div className="VideoAudioMergeNode-field">
                  <label>原音音量 ({Math.round(originalVolume * 100)}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={originalVolume}
                    onChange={(e) => setOriginalVolume(parseFloat(e.target.value))}
                    disabled={readOnly || isProcessing}
                    className="VideoAudioMergeNode-slider"
                  />
                </div>
              )}

              {/* 高级选项 */}
              <div className="VideoAudioMergeNode-advanced">
                <button
                  className="VideoAudioMergeNode-advanced-toggle"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  disabled={readOnly || isProcessing}
                >
                  高级选项 {showAdvanced ? '▼' : '▶'}
                </button>

                {showAdvanced && (
                  <div className="VideoAudioMergeNode-advanced-content">
                    <div className="VideoAudioMergeNode-field-small">
                      <label>音频偏移 (秒)</label>
                      <input
                        type="number"
                        min="0"
                        max="60"
                        step="0.1"
                        value={audioOffset}
                        onChange={(e) => setAudioOffset(parseFloat(e.target.value))}
                        disabled={readOnly || isProcessing}
                        className="VideoAudioMergeNode-input"
                      />
                    </div>

                    <div className="VideoAudioMergeNode-field-small">
                      <label>淡入时长 (秒)</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={fadeIn}
                        onChange={(e) => setFadeIn(parseFloat(e.target.value))}
                        disabled={readOnly || isProcessing}
                        className="VideoAudioMergeNode-input"
                      />
                    </div>

                    <div className="VideoAudioMergeNode-field-small">
                      <label>淡出时长 (秒)</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={fadeOut}
                        onChange={(e) => setFadeOut(parseFloat(e.target.value))}
                        disabled={readOnly || isProcessing}
                        className="VideoAudioMergeNode-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* 视频拼接模式的提示 */}
          {operationType === 'videoConcat' && (
            <div className="VideoAudioMergeNode-hint">
              💡 连接多个视频节点，按连接顺序拼接成一个视频（保留原音频）
            </div>
          )}

          {/* 处理按钮 */}
          <button
            onClick={handleProcess}
            disabled={
              readOnly || 
              isProcessing || 
              (operationType === 'audioMerge' && (!hasVideoConnection || !hasAudioConnection)) ||
              (operationType === 'videoConcat' && videoConnectionCount < 2)
            }
            className="VideoAudioMergeNode-button-primary"
            title={
              operationType === 'audioMerge'
                ? (!hasVideoConnection || !hasAudioConnection ? '请先连接视频和音频节点' : '开始合并视频和音频')
                : (videoConnectionCount < 2 ? '请至少连接2个视频节点' : '开始拼接视频')
            }
          >
            {isProcessing 
              ? '处理中...' 
              : operationType === 'audioMerge'
                ? (!hasVideoConnection || !hasAudioConnection ? '⚠️ 等待连接' : '🎵 开始合并')
                : (videoConnectionCount < 2 ? `⚠️ 需要${2 - videoConnectionCount}个视频` : `🎬 拼接${videoConnectionCount}个视频`)
            }
          </button>

          {/* 进度条 */}
          {isProcessing && (
            <div className="VideoAudioMergeNode-progress">
              <div className="VideoAudioMergeNode-progress-bar">
                <div
                  className="VideoAudioMergeNode-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="VideoAudioMergeNode-progress-text">
                {statusText} {progress > 0 && `(${Math.round(progress)}%)`}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* 输出 Handle - 用于连接到任务节点 */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="output"
        className="VideoAudioMergeNode-handle VideoAudioMergeNode-handle-output"
      >
        <div className="VideoAudioMergeNode-handle-label">输出</div>
      </Handle>
    </NodeLayout>
  );
});

VideoAudioMergeNode.displayName = 'VideoAudioMergeNode';

